import { useId, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/shared/api/client';

export interface EntitySelectProps {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  endpoint: string;
  queryKey?: string;
  displayField?: string;
  secondaryField?: string;
  required?: boolean;
  hint?: string;
  placeholder?: string;
  filter?: (row: any) => boolean;
  disabledValue?: string;
  className?: string;
}

export function EntitySelect({
  label,
  value,
  onChange,
  endpoint,
  queryKey,
  displayField = 'name',
  secondaryField,
  required,
  hint,
  placeholder = '— Selecione —',
  filter,
  disabledValue,
  className = '',
}: EntitySelectProps) {
  const reactId = useId();
  const selectId = `esel-${reactId}`;

  const list = useQuery({
    queryKey: [queryKey ?? `entity-select:${endpoint}`],
    queryFn: () => apiGet<{ items: any[] }>(`${endpoint}?limit=500`),
    staleTime: 60_000,
  });

  const items = useMemo(() => {
    const raw = list.data?.items ?? [];
    const filtered = filter ? raw.filter(filter) : raw;
    return filtered
      .map((r: any) => {
        const primary = r[displayField] ?? r.name ?? r.code ?? r.id;
        const secondary = secondaryField ? r[secondaryField] : undefined;
        return {
          id: r.id as string,
          label: secondary ? `${primary} · ${secondary}` : String(primary),
          active: r.active,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
  }, [list.data, displayField, secondaryField, filter]);

  const hasValueOption = value ? items.some((i) => i.id === value) : true;

  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      {label ? (
        <label htmlFor={selectId} className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
          {label}{required ? ' *' : ''}
        </label>
      ) : null}
      <select
        id={selectId}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={list.isLoading}
        className="h-10 rounded border border-neutral-300 bg-neutral-0 px-3 text-sm outline-none focus:border-brand-500 disabled:opacity-60 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
      >
        <option value="">{list.isLoading ? 'Carregando…' : placeholder}</option>
        {!hasValueOption && value ? (
          <option value={value}>{`(registro fora da lista) ${value.slice(0, 8)}…`}</option>
        ) : null}
        {items.map((it) => (
          <option key={it.id} value={it.id} disabled={disabledValue === it.id}>
            {it.label}{it.active === 0 ? ' (inativo)' : ''}
          </option>
        ))}
      </select>
      {list.isError ? (
        <p className="text-xs text-danger">Falha ao carregar opções.</p>
      ) : hint ? (
        <p className="text-xs text-neutral-500">{hint}</p>
      ) : null}
    </div>
  );
}
